import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";

// ==================== LAYOUT & UI ====================
import Navigation from "./components/Layout/Navigation";
import FooterLegal from "./components/Layout/FooterLegal";
import Input from "./components/UI/Input";
import LoaderLuxe from "./components/LoaderLuxe";

// ==================== VIEWS ====================
import ServicePage from "./components/Views/ServicePage";
import BoutiqueContent from "./components/Views/BoutiqueContent";
import TunnelCommande from "./components/TunnelCommande";
import AccountView from "./components/Views/AccountView";
import PagePanier from "./pages/PagePanier";
import AlcoolView from "./components/Views/AlcoolView";
import MentionsLegalesView from "./components/Views/MentionsLegalesView";
import PolitiqueConfidentialiteView from "./components/Views/PolitiqueConfidentialiteView";
import TwintTermsView from "./components/Views/TwintTermsView";
import PlateauxEvenementsView from "./components/Views/PlateauxEvenementsView";
import FondueComposerView from "./components/Views/FondueComposerView";

// ==================== HOME ====================
import HeroSection from "./components/Home/HeroSection";

// ==================== MODALS ====================
import AddProductModal from "./components/Modals/AddProductModal";
import EditProductModal from "./components/Modals/EditProductModal";
import LogoutModal from "./components/Modals/LogoutModal";
import ProductModalLuxury from "./components/Modals/ProductModalLuxury";

// ==================== DASHBOARDS ====================
import DashboardMagasin from "./dashboardmagasin/dashboardmagasin.jsx";
import AdminDashboard from "./dashboardadmin/AdminDashboard";
import AdminMilkTypes from "./dashboardadmin/AdminMilkTypes";
import AdminHomeBanner from "./dashboardadmin/AdminHomeBanner";

// ==================== CONTEXTS ====================
import { PanierContext } from "./contexts/PanierContext";
import { ProfilContext } from "./contexts/ProfilContext";

// ==================== HOOKS ====================
import { useAppLogic } from "./hooks/useAppLogic";
import { useBoutique } from "./hooks/useBoutique";
import { PricingState } from "./hooks/usePricingEngine";

// ==================== I18N ====================
import LanguageSelector from "./components/LanguageSelector";
import { translations } from "./utils/translations";

// ==================== UTILS ====================
import { auditPrice } from "./utils/priceAuditor";
import { mapProductToVM } from "./utils/mapProductToVM";
import { trackVisit } from "./utils/trackVisit";
import { trackFunnelEventBatch } from "./utils/trackFunnelBatch";

/**
 * ============================================================================
 * Application principale
 * ============================================================================
 *
 * Ce composant orchestre :
 * - les vues principales
 * - la navigation métier
 * - le panier
 * - le profil
 * - les produits boutique et cave
 * - les dashboards
 * - les modales globales
 * - la reprise produit / plateau depuis l’historique client
 */
function App() {
  useEffect(() => {
  trackVisit();

  setTimeout(() => {
    trackFunnelEventBatch("app_open");
  }, 700);
}, []);
  /**
   * ==========================================================================
   * Contextes principaux
   * ==========================================================================
   *
   * Ce bloc récupère :
   * - le panier
   * - les actions panier
   * - le profil courant
   * - les droits / pricing
   * - la déconnexion
   */
  const panierCtx = useContext(PanierContext);
  const { panier, ajouterAuPanier, viderPanier } = panierCtx;

  const ajouterAuPanierTracked = useCallback(
  (product, qty = 1, grams = 0) => {
    if (product) {
      trackFunnelEventBatch("add_to_cart", {
        product_id: product?.id || null,
        product_name: product?.name || product?.nom || "",
        category: product?.category || product?.categorie || "",
        product_scope:
          product?.__productScope ||
          (product?.__isCave === true ? "cave" : "classic"),
        is_cave: product?.__isCave === true,
        qty: qty || 1,
        grams: grams || 0
      });
    }

    return ajouterAuPanier(product, qty, grams);
  },
  [ajouterAuPanier]
);

  const { userProfil, profilPricing, authStatus, logout } =
    useContext(ProfilContext);

  /**
   * ==========================================================================
   * Hook boutique principal
   * ==========================================================================
   *
   * Ce bloc charge les produits classiques de la boutique.
   */
  const boutique = useBoutique(null, profilPricing);

  /**
   * ==========================================================================
   * Données préremplies du tunnel de commande
   * ==========================================================================
   *
   * Ce bloc hydrate les coordonnées du tunnel à partir du profil connecté.
   */
  const [checkoutUserData, setCheckoutUserData] = useState({});

  useEffect(() => {
    setCheckoutUserData((prev) => {
      const base = prev && typeof prev === "object" ? prev : {};
      const next = { ...base };

      if (userProfil && typeof userProfil === "object") {
        if (!next.prenom && userProfil.prenom) next.prenom = userProfil.prenom;
        if (!next.nom && userProfil.nom) next.nom = userProfil.nom;

        const tel = userProfil.telephone || userProfil.tel || userProfil.phone;
        if (!next.telephone && !next.tel && tel) next.telephone = tel;

        if (!next.email && userProfil.email) next.email = userProfil.email;

        if (!next.adresse && (userProfil.adresse || userProfil.address)) {
          next.adresse = userProfil.adresse || userProfil.address;
        }

        if (!next.ville && (userProfil.ville || userProfil.city)) {
          next.ville = userProfil.ville || userProfil.city;
        }

        if (!next.npa && (userProfil.npa || userProfil.zip)) {
          next.npa = userProfil.npa || userProfil.zip;
        }
      }

      return next;
    });
  }, [userProfil]);

  /**
   * ==========================================================================
   * Internationalisation
   * ==========================================================================
   *
   * Ce bloc :
   * - restaure la langue depuis le localStorage
   * - synchronise la langue active
   * - expose le dictionnaire courant
   */
  const [langueActive, setLangueActive] = useState(() => {
    const saved = localStorage.getItem("lang");
    return saved && ["fr", "de", "en"].includes(saved) ? saved : "fr";
  });

  useEffect(() => {
    localStorage.setItem("lang", langueActive);
  }, [langueActive]);

  const t = useMemo(() => {
    return translations[langueActive] || translations.fr;
  }, [langueActive]);

  /**
   * ==========================================================================
   * Installation PWA
   * ==========================================================================
   *
   * Ce bloc gère la bannière d’installation de l’application.
   */
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isIosDevice, setIsIosDevice] = useState(false);
  const [isStandaloneMode, setIsStandaloneMode] = useState(false);
  const [installMessage, setInstallMessage] = useState("");

  useEffect(() => {
    const ua = window.navigator.userAgent || "";
    const ios = /iphone|ipad|ipod/i.test(ua);

    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;

    setIsIosDevice(ios);
    setIsStandaloneMode(standalone);

    const dismissed = localStorage.getItem("pwa_install_banner_dismissed");
    const isDismissed = dismissed === "1";

    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();

      if (standalone || isDismissed) return;

      setDeferredPrompt(e);
      setShowInstallBanner(true);
    };

    const onAppInstalled = () => {
      setDeferredPrompt(null);
      setShowInstallBanner(false);
      setInstallMessage("");
      localStorage.removeItem("pwa_install_banner_dismissed");
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);

    if (!standalone && ios && !isDismissed) {
      setShowInstallBanner(true);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  const dismissInstallBanner = useCallback(() => {
    setShowInstallBanner(false);
    localStorage.setItem("pwa_install_banner_dismissed", "1");
  }, []);

  const handleInstallApp = useCallback(async () => {
    if (isIosDevice) {
      setInstallMessage(
        "Sur iPhone, appuyez sur le bouton Partager puis choisissez “Sur l’écran d’accueil”."
      );
      return;
    }

    if (!deferredPrompt) {
      setInstallMessage(
        "L’installation n’est pas encore proposée par votre navigateur. Réessayez après quelques secondes ou via le menu du navigateur."
      );
      return;
    }

    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;

      if (choice?.outcome === "accepted") {
        setInstallMessage("Installation lancée.");
      } else {
        setInstallMessage("Installation annulée.");
      }
    } catch (e) {
      setInstallMessage("Impossible de lancer l’installation.");
    } finally {
      setDeferredPrompt(null);
    }
  }, [deferredPrompt, isIosDevice]);

  const shouldShowInstallBanner = useMemo(() => {
    if (isStandaloneMode) return false;
    if (!showInstallBanner) return false;
    return Boolean(deferredPrompt) || isIosDevice;
  }, [isStandaloneMode, showInstallBanner, deferredPrompt, isIosDevice]);

  /**
   * ==========================================================================
   * Produits cave
   * ==========================================================================
   *
   * Ce bloc charge les produits cave depuis leur endpoint dédié.
   */
  const [caveRawProducts, setCaveRawProducts] = useState([]);
  const [caveLoading, setCaveLoading] = useState(false);

  const refreshCaveProducts = useCallback(async () => {
    setCaveLoading(true);

    try {
      const res = await fetch(
        "https://marchedeluxe.ch/services/get_produits_cave.php"
      );
      const data = await res.json();

      const list = Array.isArray(data?.products)
        ? data.products
        : Array.isArray(data?.produits)
        ? data.produits
        : Array.isArray(data)
        ? data
        : [];

      setCaveRawProducts(list);
    } catch (e) {
      console.error("fetchCaveProducts error:", e);
      setCaveRawProducts([]);
    } finally {
      setCaveLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      setCaveLoading(true);

      try {
        const res = await fetch(
          "https://marchedeluxe.ch/services/get_produits_cave.php"
        );
        const data = await res.json();

        const list = Array.isArray(data?.products)
          ? data.products
          : Array.isArray(data?.produits)
          ? data.produits
          : Array.isArray(data)
          ? data
          : [];

        if (!cancelled) {
          setCaveRawProducts(list);
        }
      } catch (e) {
        console.error("fetchCaveProducts error:", e);
        if (!cancelled) setCaveRawProducts([]);
      } finally {
        if (!cancelled) setCaveLoading(false);
      }
    };

    boot();

    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * ==========================================================================
   * Clés stables de recalcul
   * ==========================================================================
   *
   * Ce bloc construit des signatures utiles aux recalculs de VM / pricing.
   */
  const produitsKey = useMemo(() => {
    if (!Array.isArray(boutique.produits)) return "none";

    return boutique.produits
      .map(
        (p) =>
          `${p.id}-${p.price ?? p.prix ?? ""}-${p.isDiscount ?? ""}-${
            p.updated_at ?? ""
          }`
      )
      .join("|");
  }, [boutique.produits]);

  const profilKey = useMemo(
    () =>
      profilPricing
        ? `${profilPricing.id ?? ""}-${profilPricing.updated_at ?? ""}-${
            profilPricing.isAdmin ?? ""
          }`
        : "none",
    [profilPricing]
  );

  const userKey = useMemo(
    () =>
      userProfil
        ? `${userProfil.id ?? ""}-${userProfil.type_profil ?? ""}`
        : "none",
    [userProfil]
  );

  /**
   * ==========================================================================
   * ViewModel produits classiques
   * ==========================================================================
   *
   * Ce bloc transforme les produits classiques en VM UI avec pricing audité.
   */
  const [pricingState, setPricingState] = useState(PricingState.INIT);
  const [vmProducts, setVmProducts] = useState([]);
  const runRef = useRef(0);
  const hasReadyOnceRef = useRef(false);

  useEffect(() => {
    const runId = ++runRef.current;

    if (!Array.isArray(boutique.produits)) {
      setPricingState(PricingState.LOADING);
      return;
    }

    if (boutique.produits.length === 0) {
      setVmProducts([]);
      hasReadyOnceRef.current = true;
      setPricingState(PricingState.READY);
      return;
    }

    setPricingState(
      hasReadyOnceRef.current ? PricingState.COMPUTING : PricingState.LOADING
    );

    try {
      const computed = boutique.produits.map((p) => {
        const audit = auditPrice(p, { profilPricing, userProfil });

        return {
          ...mapProductToVM(p, audit),
          __vm: true,
          __isCave: false,
          __productScope: "classic",
        };
      });

      if (runRef.current !== runId) return;

      setVmProducts(computed);
      hasReadyOnceRef.current = true;
      setPricingState(PricingState.READY);
    } catch (e) {
      if (runRef.current !== runId) return;
      setPricingState(PricingState.ERROR);
    }
  }, [
    produitsKey,
    profilKey,
    userKey,
    profilPricing,
    userProfil,
    boutique.produits,
  ]);

  /**
   * ==========================================================================
   * ViewModel produits cave
   * ==========================================================================
   *
   * Ce bloc normalise les produits cave en VM UI cohérents.
   */
  const caveVmProducts = useMemo(() => {
    if (!Array.isArray(caveRawProducts)) return [];

    return caveRawProducts.map((p) => {
      const audit = auditPrice(p, { profilPricing, userProfil });

      const rawImage =
        p?.imageUrl || p?.image_url || p?.image_path || p?.image || "";

      const normalizedImageUrl = rawImage
        ? String(rawImage).startsWith("http")
          ? String(rawImage)
          : String(rawImage).startsWith("images/")
          ? `https://marchedeluxe.ch/${String(rawImage)}`
          : `https://marchedeluxe.ch/images/${String(rawImage)}`
        : "";

      return {
        ...mapProductToVM(
          {
            ...p,
            imageUrl: normalizedImageUrl,
            image: p?.image || rawImage,
          },
          audit
        ),
        imageUrl: normalizedImageUrl,
        __vm: true,
        __isCave: true,
        __productScope: "cave",
        product_scope: "cave",
        isCave: 1,
      };
    });
  }, [caveRawProducts, profilPricing, userProfil]);

  /**
   * ==========================================================================
   * Boutique fusionnée
   * ==========================================================================
   *
   * Ce bloc fusionne les produits classiques et cave en une seule source UI.
   */
  const boutiqueVM = useMemo(() => {
    return {
      ...boutique,
      produits: [...vmProducts, ...caveVmProducts],
    };
  }, [boutique, vmProducts, caveVmProducts]);

  /**
   * ==========================================================================
   * Logique globale de navigation
   * ==========================================================================
   *
   * Ce bloc initialise la logique applicative centrale.
   */
  const logic = useAppLogic(
    { ...boutique, produits: boutiqueVM.produits },
    { user: userProfil, authStatus },
    panierCtx,
    profilPricing
  );
const didInitDefaultViewRef = useRef(false);
useEffect(() => {
  if (didInitDefaultViewRef.current) return;
  didInitDefaultViewRef.current = true;

  const path = window.location.pathname || "/";
  const search = window.location.search || "";
  const isRootVisit = path === "/" || path === "/index.html";

  /*
   * Accès direct au domaine ou QR code principal :
   * on force l'accueil, même si une ancienne vue était stockée.
   */
  if (isRootVisit && !search) {
    localStorage.setItem("vueActuelle", "accueil");

    if (String(logic?.vueActuelle || "").trim() !== "accueil") {
      logic?.setVueActuelle?.("accueil");
    }

    return;
  }

  /*
   * Fallback legacy :
   * si aucune vue n'est sauvegardée, on ouvre l'accueil.
   */
  const savedView = String(localStorage.getItem("vueActuelle") || "").trim();
  const current = String(logic?.vueActuelle || "").trim();

  if (!savedView && current !== "accueil") {
    logic?.setVueActuelle?.("accueil");
  }
}, [logic?.vueActuelle, logic?.setVueActuelle]);

  /**
   * ==========================================================================
   * Produit actuellement sélectionné
   * ==========================================================================
   *
   * Ce bloc garantit un ViewModel correct pour la modal produit.
   */
  const selectedProductVM = useMemo(() => {
    const p = logic.selectedProduct;
    if (!p) return null;
    if (p.__vm) return p;

    const audit = auditPrice(p, { profilPricing, userProfil });
    return mapProductToVM(p, audit);
  }, [logic.selectedProduct, profilKey, userKey, profilPricing, userProfil]);

  /**
   * ==========================================================================
   * États UI globaux
   * ==========================================================================
   *
   * Ce bloc pilote :
   * - les modales globales
   * - le storytelling home
   */
  const [storyActive, setStoryActive] = useState(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [luxuryProduct, setLuxuryProduct] = useState(null);

  const openLuxuryFromBanner = useCallback((product) => {
    if (!product) return;
    setLuxuryProduct(product);
  }, []);

  const closeLuxury = useCallback(() => {
    setLuxuryProduct(null);
  }, []);

  /**
   * ==========================================================================
   * Préconfiguration plateau depuis l’historique
   * ==========================================================================
   *
   * Ce bloc conserve temporairement une configuration de plateau
   * à injecter dans la vue plateaux.
   */
  const [plateauReorderPrefill, setPlateauReorderPrefill] = useState(null);

  /**
   * ==========================================================================
   * Produits cave visibles et catégories cave
   * ==========================================================================
   *
   * Ce bloc prépare la vue cave :
   * - liste filtrée
   * - catégories affichées
   */
  const allCaveProducts = useMemo(() => {
    return boutiqueVM.produits.filter((p) => p?.__isCave === true);
  }, [boutiqueVM.produits]);

  const caveCategoriesAffichees = useMemo(() => {
    const unique = Array.from(
      new Set(
        allCaveProducts
          .map((p) => p?.category || p?.categorie || "")
          .filter((v) => String(v).trim() !== "")
      )
    );

    return unique.map((cat) => ({
      id: `cave-${cat}`,
      name: cat,
      label: cat,
      category: cat,
    }));
  }, [allCaveProducts]);

  /**
 * ==========================================================================
 * Navigation produit
 * ==========================================================================
 *
 * Ce bloc pilote l’ouverture correcte d’un produit selon son scope.
 */
const handleProductCardAction = useCallback(
  (product) => {
    if (!product) return;

    /*
     * ============================================================
     * TRACKING COGNITIF — VIEW PRODUCT
     * ============================================================
     */
    trackFunnelEventBatch("view_product", {
      product_id:
        product?.id || null,

      product_name:
        product?.name ||
        product?.nom ||
        "",

      category:
        product?.category ||
        product?.categorie ||
        "",

      product_scope:
        product?.__productScope ||
        (
          product?.__isCave === true
            ? "cave"
            : "classic"
        ),

      is_cave:
        product?.__isCave === true
    });

    if (
      product?.__isCave === true &&
      logic.vueActuelle !== "alcool"
    ) {

      logic.setCategorieActive?.(null);

      logic.setVueActuelle("alcool");

      return;
    }

    if (
      typeof logic.handleActionNavigation === "function"
    ) {

      logic.handleActionNavigation(product);
    }
  },
  [logic]
);


  /**
   * ==========================================================================
   * Construction d’une préconfiguration plateau
   * ==========================================================================
   *
   * Ce bloc extrait les données utiles d’un item historique
   * pour rouvrir correctement le configurateur.
   */
  const buildPlateauReorderPrefill = useCallback((item) => {
    if (!item || typeof item !== "object") return null;

    return {
      sourcePlateauId:
        item?.sourcePlateauId ??
        item?.produitId ??
        item?.plateauConfig?.plateau_id ??
        null,

      sourcePlateauSlug:
        item?.sourcePlateauSlug ?? item?.plateauConfig?.plateau_slug ?? null,

      sourcePlateauCode:
        item?.sourcePlateauCode ?? item?.plateauConfig?.plateau_code ?? null,

      personnes:
        item?.plateau_personnes ??
        item?.options?.personnes ??
        item?.plateauConfig?.personnes ??
        item?.composition?.personnes ??
        2,

      fromages:
        item?.plateau_fromages ??
        item?.options?.fromages ??
        item?.plateauConfig?.fromages ??
        item?.composition?.fromages ??
        "",

      laitsEviter:
        item?.plateau_laits_eviter ??
        item?.options?.laitsEviter ??
        item?.plateauConfig?.laitsEviter ??
        item?.composition?.laitsEviter ??
        [],

      detailsLabel: item?.details_label || "",
      reorderToken: Date.now(),
    };
  }, []);

  /**
   * ==========================================================================
   * Réajout d’un produit simple depuis l’historique
   * ==========================================================================
   *
   * Ce bloc retrouve le produit actuel dans le catalogue courant,
   * puis l’ajoute au panier avec le pricing actif.
   */
  const handleReorderSimpleProductFromHistory = useCallback(
    async (item) => {
      if (!item || typeof item !== "object") {
        return {
          success: false,
          message: "Produit introuvable dans l’historique.",
        };
      }

      const wantedId = Number(item?.id ?? item?.product_id ?? item?.productId);
      if (!Number.isFinite(wantedId) || wantedId <= 0) {
        return {
          success: false,
          message: "Produit introuvable dans l’historique.",
        };
      }

      const sourceTable = String(item?.source_table || "")
        .trim()
        .toLowerCase();

      const currentProduct =
        boutiqueVM.produits.find((p) => {
          if (Number(p?.id) !== wantedId) return false;

          if (sourceTable === "wyei_marche_produits_cave") {
            return p?.__productScope === "cave";
          }

          if (sourceTable === "wyei_marche_produits") {
            return p?.__productScope !== "cave";
          }

          return true;
        }) || null;

      if (!currentProduct) {
        return {
          success: false,
          message:
            "Ce produit n’est plus disponible actuellement dans le catalogue.",
        };
      }

      if (!currentProduct?.isVendableUI) {
        return {
          success: false,
          message:
            "Ce produit n’est pas disponible actuellement pour ajout au panier.",
        };
      }

      const desiredQty =
        Number(item?.qty ?? item?.quantite ?? item?.quantity ?? 1) || 1;
      const grams = Number(item?.grams || 0) || 0;

      let finalQty = desiredQty;

      if (grams <= 0 && Number.isFinite(Number(currentProduct?.stock))) {
        const stock = Number(currentProduct.stock);

        if (stock <= 0) {
          return {
            success: false,
            message:
              "Ce produit n’est plus disponible actuellement pour ajout au panier.",
          };
        }

        finalQty = Math.min(desiredQty, stock);
      }

      ajouterAuPanierTracked(currentProduct, finalQty, grams);

      if (finalQty < desiredQty) {
        return {
          success: true,
          message: `Produit ajouté au panier avec quantité ajustée à ${finalQty}.`,
        };
      }

      return {
        success: true,
        message: "Produit ajouté au panier.",
      };
    },
    [ajouterAuPanierTracked, boutiqueVM.produits]
  );

  /**
   * ==========================================================================
   * Reconfiguration d’un plateau depuis l’historique
   * ==========================================================================
   *
   * Ce bloc prépare la configuration puis ouvre la vue plateaux.
   */
  const handleReconfigurePlateauFromHistory = useCallback(
    async (item) => {
      const prefill = buildPlateauReorderPrefill(item);

      if (!prefill?.sourcePlateauId && !prefill?.sourcePlateauSlug) {
        return {
          success: false,
          message: "Impossible de reconfigurer ce plateau.",
        };
      }

      setPlateauReorderPrefill(prefill);
      logic.setVueActuelle("plateaux-evenements");

      return {
        success: true,
        message: "Ouverture du configurateur de plateau.",
      };
    },
    [buildPlateauReorderPrefill, logic]
  );

  /**
   * ==========================================================================
   * Droits et rôles
   * ==========================================================================
   *
   * Ce bloc calcule les droits utiles aux dashboards et aux vues admin.
   */
  const isRoleResolved = authStatus === "connected" && !!profilPricing;
  const isAdmin = isRoleResolved ? !!profilPricing?.isAdmin : false;
  const isCollaborator = isRoleResolved
    ? !!profilPricing?.isCollaborator
    : false;

  const canSeeDashboardMagasin = useMemo(
    () =>
      authStatus === "connected" &&
      (isAdmin || isCollaborator) &&
      logic.vueActuelle === "dashboard-magasin",
    [authStatus, isAdmin, isCollaborator, logic.vueActuelle]
  );

  const canSeeAdmin = useMemo(
    () =>
      authStatus === "connected" && isAdmin && logic.vueActuelle === "admin",
    [authStatus, isAdmin, logic.vueActuelle]
  );

  const canSeeAdminMilkTypes = useMemo(
    () =>
      authStatus === "connected" &&
      isAdmin &&
      logic.vueActuelle === "admin-milk-types",
    [authStatus, isAdmin, logic.vueActuelle]
  );

  const canSeeAdminHomeBanner = useMemo(
    () =>
      authStatus === "connected" &&
      isAdmin &&
      logic.vueActuelle === "admin-home-banner",
    [authStatus, isAdmin, logic.vueActuelle]
  );

  /**
   * ==========================================================================
   * États et outils admin inventaire
   * ==========================================================================
   *
   * Ce bloc pilote :
   * - chargement inventaire
   * - stats
   * - recherche
   * - tri
   * - modales add/edit
   */
  const [adminProducts, setAdminProducts] = useState([]);
  const [adminStats, setAdminStats] = useState({
    total: 0,
    ruptures: 0,
    promos: 0,
  });

  const [adminSearch, setAdminSearch] = useState("");
  const [adminCategory, setAdminCategory] = useState("");

  const [adminSortConfig, setAdminSortConfig] = useState({
    key: "name",
    direction: "asc",
  });

  const [adminLoading, setAdminLoading] = useState(false);
  const [adminError, setAdminError] = useState("");

  const [showAdminAddModal, setShowAdminAddModal] = useState(false);
  const [editFormP, setEditFormP] = useState(null);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [adminProductScope, setAdminProductScope] = useState("classic");

  /**
   * ==========================================================================
   * URL admin produits
   * ==========================================================================
   *
   * Ce bloc construit l’URL de chargement admin selon recherche et tri.
   */
  const buildAdminProductsUrl = useCallback(() => {
    const params = new URLSearchParams();

    if (adminSearch) params.set("search", adminSearch);
    if (adminSortConfig?.key) params.set("sort", adminSortConfig.key);
    if (adminSortConfig?.direction) {
      params.set("direction", adminSortConfig.direction);
    }

    return `https://marchedeluxe.ch/services/get_produits.php?${params.toString()}`;
  }, [adminSearch, adminSortConfig]);

  /**
   * ==========================================================================
   * Chargement des stats admin
   * ==========================================================================
   *
   * Ce bloc charge les métriques inventaire.
   */
  const fetchAdminStats = useCallback(async () => {
    try {
      const res = await fetch(
        "https://marchedeluxe.ch/services/get_admin_stats.php"
      );
      const data = await res.json();

      return {
        total: Number(data?.total ?? 0),
        ruptures: Number(data?.ruptures ?? 0),
        promos: Number(data?.promos ?? 0),
      };
    } catch {
      return { total: 0, ruptures: 0, promos: 0 };
    }
  }, []);

  /**
   * ==========================================================================
   * Chargement des produits admin
   * ==========================================================================
   *
   * Ce bloc charge et fusionne les produits classiques et cave côté admin.
   */
  const fetchAdminProducts = useCallback(async () => {
    setAdminLoading(true);
    setAdminError("");

    try {
      const token = localStorage.getItem("user_token") || "";
      const classicUrl = buildAdminProductsUrl();

      const [classicRes, caveRes] = await Promise.all([
        fetch(classicUrl, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch("https://marchedeluxe.ch/services/get_produits_cave.php", {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (classicRes.status === 401 || caveRes.status === 401) {
        setAdminError("Session expirée. Merci de vous reconnecter.");
        setAdminProducts([]);
        return;
      }

      const classicData = await classicRes.json();
      const caveData = await caveRes.json();

      const classicList = Array.isArray(classicData?.products)
        ? classicData.products
        : Array.isArray(classicData?.produits)
        ? classicData.produits
        : Array.isArray(classicData)
        ? classicData
        : [];

      const caveListRaw = Array.isArray(caveData?.products)
        ? caveData.products
        : Array.isArray(caveData?.produits)
        ? caveData.produits
        : Array.isArray(caveData)
        ? caveData
        : [];

      const caveList = caveListRaw.map((p) => ({
        ...p,
        __isCave: true,
        __productScope: "cave",
        product_scope: "cave",
        isCave: 1,
        image: p?.image || p?.image_url || "",
      }));

      const classicNormalized = classicList.map((p) => ({
        ...p,
        __isCave: false,
        __productScope: "classic",
        product_scope: "classic",
        isCave: 0,
      }));

      setAdminProducts([...classicNormalized, ...caveList]);

      const s = await fetchAdminStats();
      setAdminStats(s);
    } catch (e) {
      console.error("fetchAdminProducts error:", e);
      setAdminError(e?.message || "Erreur chargement inventaire.");
      setAdminProducts([]);
    } finally {
      setAdminLoading(false);
    }
  }, [buildAdminProductsUrl, fetchAdminStats]);

  useEffect(() => {
    if (canSeeAdmin) fetchAdminProducts();
  }, [canSeeAdmin, fetchAdminProducts]);

  useEffect(() => {
    if (canSeeAdmin) fetchAdminProducts();
  }, [canSeeAdmin, adminSearch, adminSortConfig, fetchAdminProducts]);

  /**
   * ==========================================================================
   * Tri admin
   * ==========================================================================
   *
   * Ce bloc inverse ou définit le tri côté admin.
   */
  const onAdminSort = useCallback((key) => {
    setAdminSortConfig((prev) => {
      const direction =
        prev.key === key ? (prev.direction === "asc" ? "desc" : "asc") : "asc";

      return { key, direction };
    });
  }, []);

  /**
   * ==========================================================================
   * Ouverture modal ajout admin
   * ==========================================================================
   *
   * Ce bloc prépare le scope de création produit.
   */
  const handleAdminAdd = useCallback((scope = "classic") => {
    setAdminProductScope(scope === "cave" ? "cave" : "classic");
    setShowAdminAddModal(true);
  }, []);

  /**
   * ==========================================================================
   * Ouverture modal édition admin
   * ==========================================================================
   *
   * Ce bloc prépare le scope et le payload d’édition.
   */
  const handleAdminEdit = useCallback((productVM, scope = "classic") => {
    if (!productVM) return;

    const finalScope =
      scope === "cave" ||
      productVM?.__productScope === "cave" ||
      productVM?.product_scope === "cave" ||
      productVM?.__isCave === true ||
      Number(productVM?.isCave || 0) === 1
        ? "cave"
        : "classic";

    setAdminProductScope(finalScope);

    setEditFormP({
      ...productVM,
      __productScope: finalScope,
      __isCave: finalScope === "cave",
      product_scope: finalScope,
      isCave: finalScope === "cave" ? 1 : 0,
    });
  }, []);

  /**
   * ==========================================================================
   * Sauvegarde édition admin
   * ==========================================================================
   *
   * Ce bloc enregistre les modifications produit côté admin.
   */
const handleSaveEdit = useCallback(async (overrideForm = null) => {
  const formToSave = overrideForm || editFormP;
  if (!formToSave?.id) return;

  setIsEditSubmitting(true);
  setAdminError("");

  try {
    const token = localStorage.getItem("user_token") || "";

    const finalCategory =
      formToSave.category === "NEW"
        ? formToSave.newCategoryName
        : formToSave.category;

    const isCaveEdit =
      adminProductScope === "cave" ||
      formToSave?.__productScope === "cave" ||
      formToSave?.product_scope === "cave" ||
      formToSave?.__isCave === true ||
      Number(formToSave?.isCave || 0) === 1;

    const payload = {
      complet: true,
      id: formToSave.id,
      name: (formToSave.name || "").trim(),
      category: (finalCategory || formToSave.category || "").trim(),
      price: Number(formToSave.price ?? 0) || 0,
      old_price: Number(formToSave.old_price ?? 0) || 0,
      histoire: formToSave.histoire || "",
      unite: formToSave.unite || "KG",
      isDiscount: Number(formToSave.isDiscount ?? 0) || 0,
      isOnline: Number(formToSave.isOnline ?? 0) || 0,
      stock: Number(formToSave.stock ?? 0) || 0,

      origine: formToSave.origine || "",
      texture: formToSave.texture || "",
      intensite: formToSave.intensite || "",

      millesime: formToSave.millesime || "",
      region: formToSave.region || "",
      cepage: formToSave.cepage || "",

      image_url:
        formToSave.image_url ||
        formToSave.imageUrl ||
        formToSave.image ||
        formToSave.urlImage ||
        "",

      product_scope: isCaveEdit ? "cave" : "classic",
      isCave: isCaveEdit ? 1 : 0,
    };

    const updateUrl = isCaveEdit
      ? "https://marchedeluxe.ch/services/modifier_produit_cave.php"
      : "https://marchedeluxe.ch/services/update_produit_general.php";

    const res = await fetch(updateUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    const data = await res.json();

    if (!data?.success) {
      setAdminError(data?.error || data?.message || "Update impossible.");
      return;
    }

    if (formToSave.imageFile) {
      const fd = new FormData();
      fd.append("id", String(formToSave.id));
      fd.append("image", formToSave.imageFile);
      fd.append("product_scope", isCaveEdit ? "cave" : "classic");

      const imgRes = await fetch(
        "https://marchedeluxe.ch/services/upload_image.php",
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        }
      );

      const imgData = await imgRes.json();

      if (!imgData?.success) {
        setAdminError(imgData?.message || "Upload image impossible.");
        return;
      }
    }

    await fetchAdminProducts();
    await refreshCaveProducts();
    setEditFormP(null);
  } catch (e) {
    setAdminError(e?.message || "Erreur réseau (édition).");
  } finally {
    setIsEditSubmitting(false);
  }
}, [editFormP, adminProductScope, fetchAdminProducts, refreshCaveProducts]);

  /**
   * ==========================================================================
   * Suppression admin
   * ==========================================================================
   *
   * Ce bloc supprime un produit classique ou cave côté admin.
   */
  const handleAdminDelete = useCallback(
    async (productId, scope = "classic") => {
      if (!productId) return;

      try {
        const token = localStorage.getItem("user_token") || "";
        const isCaveDelete = scope === "cave";

        const deleteUrl = isCaveDelete
          ? "https://marchedeluxe.ch/services/supprimer_produit_cave.php"
          : "https://marchedeluxe.ch/services/supprimer_produit.php";

        const res = await fetch(deleteUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ id: productId }),
        });

        const data = await res.json();

        if (!data?.success) {
          setAdminError(
            data?.error || data?.message || "Suppression impossible."
          );
          return;
        }

        await fetchAdminProducts();
        await refreshCaveProducts();
      } catch (e) {
        setAdminError(e?.message || "Erreur réseau suppression.");
      }
    },
    [fetchAdminProducts, refreshCaveProducts]
  );

  /**
   * ==========================================================================
   * Toggle stock admin
   * ==========================================================================
   *
   * Ce bloc change rapidement l’état de stock côté admin.
   */
  const handleAdminToggleStock = useCallback(
    async (productId, currentStock) => {
      try {
        const token = localStorage.getItem("user_token") || "";
        const nextStock = Number(currentStock) > 0 ? 0 : 1;

        const res = await fetch(
          "https://marchedeluxe.ch/services/update_stock.php",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ id: productId, stock: nextStock }),
          }
        );

        const data = await res.json();

        if (!data?.success) {
          setAdminError(
            data?.error || data?.message || "Update stock impossible."
          );
          return;
        }

        fetchAdminProducts();
      } catch (e) {
        setAdminError(e?.message || "Failed to fetch.");
      }
    },
    [fetchAdminProducts]
  );

  /**
   * ==========================================================================
   * Toggle online admin
   * ==========================================================================
   *
   * Ce bloc change l’état en ligne d’un produit côté admin.
   */
  const handleAdminToggleOnline = useCallback(
    async (productId, nextIsOnline) => {
      try {
        const token = localStorage.getItem("user_token") || "";

        const res = await fetch(
          "https://marchedeluxe.ch/services/update_produit_general.php",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              id: productId,
              champ: "isOnline",
              valeur: Number(nextIsOnline),
            }),
          }
        );

        const data = await res.json();

        if (!data?.success) {
          setAdminError(
            data?.error || data?.message || "Update isOnline impossible."
          );
          return;
        }

        fetchAdminProducts();
      } catch (e) {
        setAdminError(e?.message || "Erreur réseau isOnline.");
      }
    },
    [fetchAdminProducts]
  );

  /**
   * ==========================================================================
   * Toggle promo admin
   * ==========================================================================
   *
   * Ce bloc change l’état promo d’un produit côté admin.
   */
  const handleAdminTogglePromo = useCallback(
    async (productId, nextIsDiscount) => {
      try {
        const token = localStorage.getItem("user_token") || "";

        const res = await fetch(
          "https://marchedeluxe.ch/services/update_produit_general.php",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              id: productId,
              champ: "isDiscount",
              valeur: Number(nextIsDiscount),
            }),
          }
        );

        const data = await res.json();

        if (!data?.success) {
          setAdminError(
            data?.error || data?.message || "Update promo impossible."
          );
          return;
        }

        fetchAdminProducts();
      } catch (e) {
        setAdminError(e?.message || "Erreur réseau promo.");
      }
    },
    [fetchAdminProducts]
  );

  /**
   * ==========================================================================
   * Confirmation de déconnexion
   * ==========================================================================
   *
   * Ce bloc ferme la modal, vide le panier, déconnecte
   * puis renvoie l’utilisateur à l’accueil.
   */
  const handleConfirmLogout = useCallback(() => {
    setShowLogoutModal(false);

    if (typeof viderPanier === "function") {
      viderPanier();
    }

    if (typeof logout === "function") {
      logout();
    }

    setTimeout(() => {
      logic.setVueActuelle("accueil");
    }, 0);
  }, [viderPanier, logout, logic]);

  /**
   * ==========================================================================
   * Loader global
   * ==========================================================================
   *
   * Ce bloc bloque l’interface tant que l’auth ou le pricing ne sont pas prêts.
   */
  const showGlobalLoader =
    authStatus === "loading" ||
    (!hasReadyOnceRef.current && pricingState !== PricingState.READY);

  if (showGlobalLoader) return <LoaderLuxe />;

  /**
   * ==========================================================================
   * Rendu principal
   * ==========================================================================
   *
   * Ce bloc distribue les vues, modales et composants globaux.
   */
  return (
    <div className="App">
      <LanguageSelector
        langueActive={langueActive}
        onChangeLangue={setLangueActive}
        vueActuelle={logic.vueActuelle}
      />

      <Navigation
        t={t}
        authStatus={authStatus}
        userProfil={userProfil}
        vueActuelle={logic.vueActuelle}
        resetVues={boutique.resetVues}
        setVueActuelle={logic.setVueActuelle}
        setCategorieActive={logic.setCategorieActive}
        isAdmin={isAdmin}
        isCollaborator={isCollaborator}
        onLogout={() =>
          panier.length > 0 ? setShowLogoutModal(true) : handleConfirmLogout()
        }
      />

      {shouldShowInstallBanner ? (
        <div
          style={{
            position: "fixed",
            left: 16,
            right: 16,
            bottom: 16,
            zIndex: 9999,
            maxWidth: 560,
            margin: "0 auto",
            padding: "14px 16px",
            borderRadius: 18,
            background: "rgba(255,255,255,0.96)",
            border: "1px solid rgba(212,175,55,0.28)",
            boxShadow: "0 14px 38px rgba(0,0,0,0.16)",
            backdropFilter: "blur(10px)",
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "#8e7440",
              marginBottom: 8,
            }}
          >
            Application Marché Deluxe
          </div>

          <div
            style={{
              fontSize: 14,
              lineHeight: 1.55,
              color: "#1f1a17",
              marginBottom: 12,
            }}
          >
            {isIosDevice
              ? "Installez l’application sur votre iPhone pour accéder plus vite à Marché Deluxe."
              : "Installez l’application pour ouvrir Marché Deluxe directement depuis votre téléphone."}
          </div>

          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              className="btn-luxury"
              onClick={handleInstallApp}
            >
              {isIosDevice
                ? "Voir comment installer"
                : "Installer l’application"}
            </button>

            <button
              type="button"
              className="btn-luxury-outline"
              onClick={dismissInstallBanner}
            >
              Plus tard
            </button>
          </div>

          {installMessage ? (
            <div
              style={{
                marginTop: 10,
                fontSize: 12,
                lineHeight: 1.5,
                color: "#5a4b3f",
              }}
            >
              {installMessage}
            </div>
          ) : null}
        </div>
      ) : null}

      <main>
        {["accueil", "magasin", "produits", "actions"].includes(
          logic.vueActuelle
        ) && (
          <>
            {logic.vueActuelle === "accueil" && (
              <HeroSection
                t={t}
                setVueActuelle={logic.setVueActuelle}
                produitsAffiches={logic.produitsAffiches}
                handleActionNavigation={openLuxuryFromBanner}
                setStoryActive={setStoryActive}
                produits={boutiqueVM.produits}
              />
            )}

            {logic.vueActuelle !== "accueil" && (
              <div className="main-content page-transition">
                {logic.categorieActive && (
                  <div className="category-nav-back">
                    <span
                      onClick={() => logic.setCategorieActive(null)}
                      className="back-link"
                    >
                      ← {t.allProducts}
                    </span>
                  </div>
                )}

                <div className="shop-header">
                  <h1>
                    {logic.categorieActive ||
                      (logic.vueActuelle === "actions" ? t.actions : t.shop)}
                  </h1>

                  <Input
                    placeholder={t.search}
                    value={logic.recherche}
                    onChange={(e) => logic.setRecherche(e.target.value)}
                    className="max-w-md"
                  />
                </div>

                <BoutiqueContent
                  {...logic}
                  t={t}
                  boutique={boutiqueVM}
                  produitsAffiches={logic.produitsAffiches}
                  ajouterAuPanier={ajouterAuPanierTracked}
                  isProfilReady={true}
                  profilPricing={profilPricing}
                  isLoading={boutique.isLoading || caveLoading}
                  handleProductClick={handleProductCardAction}
                  setVueActuelle={logic.setVueActuelle}
                />
              </div>
            )}
          </>
        )}

        {logic.vueActuelle === "service" && (
          <ServicePage t={t} setVueActuelle={logic.setVueActuelle} />
        )}
        {logic.vueActuelle === "fondue-composer" && (
          <FondueComposerView
            t={t}
            setVueActuelle={logic.setVueActuelle}
            produits={logic.produits || logic.produitsAffiches || []}
          />
        )}

        {logic.vueActuelle === "alcool" && (
          <div className="main-content page-transition">
            <AlcoolView
              t={t}
              setVueActuelle={logic.setVueActuelle}
              categorieActive={logic.categorieActive}
              onBackCategories={() => logic.setCategorieActive(null)}
            >
              <BoutiqueContent
                vueActuelle="alcool"
                categorieActive={logic.categorieActive}
                categoriesAffichees={caveCategoriesAffichees}
                catLoading={caveLoading}
                setCategorieActive={logic.setCategorieActive}
                setVueActuelle={logic.setVueActuelle}
                t={t}
                boutique={boutiqueVM}
                produitsAffiches={allCaveProducts}
                ajouterAuPanier={ajouterAuPanierTracked}
                isProfilReady={true}
                profilPricing={profilPricing}
                isLoading={caveLoading}
                handleProductClick={handleProductCardAction}
              />
            </AlcoolView>
          </div>
        )}

        {logic.vueActuelle === "tunnel" && (
          <div className="main-content page-transition">
            <TunnelCommande
              userData={checkoutUserData}
              setUserData={setCheckoutUserData}
              setVue={logic.setVueActuelle}
              t={t}
            />
          </div>
        )}

        {logic.vueActuelle === "compte" && (
          <div className="main-content page-transition">
            <AccountView
              t={t}
              onLogoutRequest={() => {
                if (panier?.length > 0) {
                  setShowLogoutModal(true);
                  return;
                }

                handleConfirmLogout();
              }}
              onReorderSimpleProduct={handleReorderSimpleProductFromHistory}
              onReconfigurePlateau={handleReconfigurePlateauFromHistory}
            />
          </div>
        )}

        {logic.vueActuelle === "panier" && (
          <div className="main-content page-transition">
            <PagePanier t={t} setVue={logic.setVueActuelle} />
          </div>
        )}

        {logic.vueActuelle === "mentions-legales" && (
          <div className="main-content page-transition">
            <MentionsLegalesView />
          </div>
        )}

        {logic.vueActuelle === "politique-confidentialite" && (
          <div className="main-content page-transition">
            <PolitiqueConfidentialiteView />
          </div>
        )}

        {logic.vueActuelle === "twint-terms" && (
          <div className="main-content page-transition">
            <TwintTermsView />
          </div>
        )}

        {logic.vueActuelle === "plateaux-evenements" && (
          <PlateauxEvenementsView
            t={t}
            initialReorderConfig={plateauReorderPrefill}
            onReorderConfigConsumed={() => setPlateauReorderPrefill(null)}
            setVueActuelle={logic.setVueActuelle}
          />
        )}
        {canSeeAdmin && (
          <div className="main-content page-transition">
            {adminError ? (
              <div
                style={{
                  marginBottom: 12,
                  padding: 12,
                  borderRadius: 12,
                  border: "1px solid rgba(255,0,0,.25)",
                  background: "rgba(255,0,0,.06)",
                }}
              >
                <strong>Erreur :</strong> {adminError}
              </div>
            ) : null}

            {adminLoading ? (
              <div style={{ padding: 20, opacity: 0.8 }}>
                Chargement inventaire…
              </div>
            ) : null}

            <AdminDashboard
              stats={adminStats}
              products={adminProducts}
              recherche={adminSearch}
              setRecherche={setAdminSearch}
              categoryFilter={adminCategory}
              setCategoryFilter={setAdminCategory}
              onAdd={handleAdminAdd}
              onSort={onAdminSort}
              sortConfig={adminSortConfig}
              onToggleStock={handleAdminToggleStock}
              onToggleOnline={handleAdminToggleOnline}
              onTogglePromo={handleAdminTogglePromo}
              onEdit={handleAdminEdit}
              onDelete={handleAdminDelete}
              onOpenHomeBanner={() => logic.setVueActuelle("admin-home-banner")}
              mapProductToVM={mapProductToVM}
            />
          </div>
        )}

        {canSeeAdminMilkTypes && (
          <div className="main-content page-transition">
            <AdminMilkTypes />
          </div>
        )}

        {canSeeAdminHomeBanner && (
          <div className="main-content page-transition">
            <AdminHomeBanner />
          </div>
        )}

        {canSeeDashboardMagasin && (
          <div className="main-content page-transition">
            <DashboardMagasin />
          </div>
        )}
      </main>

      {logic.vueActuelle === "accueil" && (
        <FooterLegal setVueActuelle={logic.setVueActuelle} />
      )}

      <AddProductModal
        showAddModal={showAdminAddModal}
        productScope={adminProductScope}
        setShowAddModal={(v) => {
          setShowAdminAddModal(v);

          if (!v) {
            fetchAdminProducts();
            refreshCaveProducts();
          }
        }}
      />

      <EditProductModal
        formP={editFormP}
        productScope={adminProductScope}
        setFormP={setEditFormP}
        handleSave={handleSaveEdit}
        isSubmitting={isEditSubmitting}
        closeModal={() => setEditFormP(null)}
      />

      {showLogoutModal && (
        <LogoutModal
          show={showLogoutModal}
          setShow={setShowLogoutModal}
          panier={panier}
          handleConfirmLogout={handleConfirmLogout}
          t={t}
        />
      )}

      {luxuryProduct && (
        <ProductModalLuxury
        product={luxuryProduct}
        onClose={closeLuxury}
        onUnlock={() => logic.setVueActuelle("compte")}
        onAddToCart={(enrichedItem) => {
          const item =
           enrichedItem || luxuryProduct;

          /*
           * ============================================================
           * TRACKING COGNITIF — ADD TO CART
           * ============================================================
           */
          trackFunnelEventBatch("add_to_cart", {
            product_id:
             item?.id || null,

            product_name:
             item?.name ||
             item?.nom ||
             "",

            category:
             item?.category ||
             item?.categorie ||
             "",

            product_scope:
             item?.__productScope ||
             ( 
             item?.__isCave === true
                ? "cave"
                : "classic"
             ),

            is_cave:
             item?.__isCave === true,
            
            qty:
             item?.qty || 1,
            
             grams:
              item?.grams || 0
          
          });

           /*
            * ============================================================
            * AJOUT PANIER HISTORIQUE
            * ============================================================
            */
          ajouterAuPanier(item);
          closeLuxury();
        }}
        t={t}
      />
    )}

      {selectedProductVM ? null : null}

      {logic.catLoading && <LoaderLuxe />}
    </div>
  );
}

export default App;
